// İlk önce CSS import
import '../../../context/AuthContext.css';

// Sonra React ve diğer kütüphane importları
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import AccountSwitcher from '../../../components/AccountSwitcher';
import DateRangeFilter from '../../../components/filters/DateRangeFilter';
import Ga4WebOnlyNote from '../../../components/Ga4WebOnlyNote';
import { useAccounts } from '../../../context/AccountContext';
import { Card } from '../../../components/ui/Card';
import {
  Filter,
  Download,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronRight,
  LayoutList,
  TrendingUp,
  DollarSign,
  MousePointerClick,
  ShoppingCart,
  Video,
  Calendar,
  Play,
  ExternalLink,
  Check,
  Loader2,
  Edit2,
  X,
  AlertTriangle,
  Activity,
  History,
  Clock,
  XCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Sample ad groups data structure
// Bu eski veri yapısını kaldırıyoruz, çünkü artık API'den gerçek veri çekeceğiz.

// Sample chart data
// Bu eski veri yapısını da kaldırıyoruz.
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
      },
      ticks: {
        callback: (value) => `₺${value}`
      }
    },
    x: {
      grid: {
        display: false
      }
    },
    y2: {
      position: 'right',
      grid: { drawOnChartArea: false }
    }
  },
  plugins: {
    legend: {
      position: 'top',
      align: 'end',
      labels: {
        boxWidth: 10,
        usePointStyle: true,
        pointStyle: 'circle'
      }
    },
    tooltip: {
      callbacks: {
        label: function(context) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          if (context.parsed.y !== null) {
            label += `₺${context.parsed.y.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
          }
          return label;
        }
      }
    }
  },
  interaction: {
    intersect: false,
    mode: 'index',
  }
};

// A brand counts as "demo" when it is the canonical demo brand, has DEMO status,
// or its name contains "demo" (covers the various *Demo Store* test brands).
// Reads the cached selectedBrand so it works regardless of which demo brand is active.
const CANONICAL_DEMO_BRAND_ID = 'demo-brand-glowytics-2024';
function isDemoBrandId(brandId) {
  if (!brandId) return false;
  if (brandId === CANONICAL_DEMO_BRAND_ID) return true;
  try {
    const b = JSON.parse(localStorage.getItem('selectedBrand') || 'null');
    if (b && b.id === brandId) {
      if (String(b.status || '').toUpperCase() === 'DEMO') return true;
      if (/demo/i.test(b.name || '')) return true;
    }
  } catch (_) {}
  return false;
}

// Static YouTube video previews for demo brands. Real, embeddable video IDs so
// the img.youtube.com thumbnails resolve; paired with the same campaign names the
// dummy performance/placement datasets use, so the page reads coherently.
function buildDummyVideoPreviews() {
  return [
    { videoId: 'aqz-KE-bpKQ', campaignName: 'YouTube - Brand Awareness' },
    { videoId: 'ScMzIvxBSi4', campaignName: 'YouTube - Product Launch' },
    { videoId: 'M7lc1UVf-VE', campaignName: 'YouTube - Retargeting' },
    { videoId: 'ysz5S6PUM-U', campaignName: 'YouTube - Prospecting' },
    { videoId: '6stlCkUDG_s', campaignName: 'YouTube - Seasonal Campaign' },
    { videoId: 'YE7VzlLtp-4', campaignName: 'Video - In-Stream Ads' },
    { videoId: '_OBlgSz8sSM', campaignName: 'Video - Discovery Ads' },
    { videoId: 'kXYiU_JCYtU', campaignName: 'YouTube - Competitor Conquest' },
    { videoId: 'e-ORhEE9VVg', campaignName: 'YouTube - Brand Awareness' },
    { videoId: 'RgKAFK5djSk', campaignName: 'YouTube - Product Launch' },
    { videoId: 'fLexgOxsZu0', campaignName: 'Video - In-Stream Ads' },
    { videoId: 'C0DPdy98e4c', campaignName: 'YouTube - Prospecting' },
  ];
}

export default function VideoCampaigns() {
  const { selectedBrand } = useAuth();
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('GOOGLE_ADS');

  const [liveDataMap, setLiveDataMap] = useState({});
  const [isLoadingLive, setIsLoadingLive] = useState(false);
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [editBudget, setEditBudget] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Change History state
  const [showChangeHistory, setShowChangeHistory] = useState(false);
  const [changeHistoryData, setChangeHistoryData] = useState([]);
  const [changeHistoryTotal, setChangeHistoryTotal] = useState(0);
  const [changeHistoryLoading, setChangeHistoryLoading] = useState(false);
  const [changeHistoryFilter, setChangeHistoryFilter] = useState('all');

  const [isLoading, setIsLoading] = useState(false);
  const [dateRange, setDateRange] = useState('last30');
  const [gaAccountFilter, setGaAccountFilter] = useState('all');
  const [gaAccounts, setGaAccounts] = useState([]);
  const [videoData, setVideoData] = useState([]); // keywordData -> videoData
  const [campaigns, setCampaigns] = useState([]);
  const [expandedCampaigns, setExpandedCampaigns] = useState(new Set());
  const [expandedAdGroups, setExpandedAdGroups] = useState(new Set());
  // Sıralama için state değişkenleri
  const [sortField, setSortField] = useState('cost'); // Varsayılan sıralama alanı
  const [sortDirection, setSortDirection] = useState('desc'); // Varsayılan sıralama yönü
  // Monthly breakdown
  const [monthlyData, setMonthlyData] = useState([]);
  const [monthlySortField, setMonthlySortField] = useState('month');
  const [monthlySortDirection, setMonthlySortDirection] = useState('desc');
  // Chart verisi için state
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Video Views', // Label güncellendi
        data: [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y',
      },
      {
        label: 'Cost', // Label güncellendi
        data: [],
        borderColor: 'rgb(239, 68, 68)', // Renk kırmızı tonu
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y2', // İkinci Y ekseni
      }
    ]
  });

  // Add missing state variables
  const [trendData, setTrendData] = useState([]);
  const [isLoadingTrend, setIsLoadingTrend] = useState(false);

  // Update the state to include the new filter option
  const [selectedDateRange, setSelectedDateRange] = useState('7days');

  // State kısmına dataLoading ekleyelim
  const [dataLoading, setDataLoading] = useState(true);

  // Date Filter Dropdown state ekleyin
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);

  // Diğer state tanımlamalarının yanına ekleyelim
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  // Expand/collapse for Top Categories children (top channels per category)
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [youtubeData, setYoutubeData] = useState(null);
  const [isYoutubeLoading, setIsYoutubeLoading] = useState(false);
  const [videoPreviews, setVideoPreviews] = useState([]); // { videoId, campaignName } from live API
  const [videoPreviewsLoading, setVideoPreviewsLoading] = useState(false);
  const [ytRetryCount, setYtRetryCount] = useState(0);
  const ytRetryTimer = useRef(null);
  const DEMO_BRAND_ID_SC = '2b08baa8-dd8e-4b5c-93cd-dd38b9b3718a';

  // Immediate dummy hydrate for the specific brand to avoid empty UI before fetch completes
  useEffect(() => {
    const brandId = localStorage.getItem('currentBrandId');
    if (isDemoBrandId(brandId)) {
      setVideoData(prev => (Array.isArray(prev) && prev.length > 0) ? prev : buildDummyVideoData());
      setYoutubeData(prev => {
        const has = !!(Array.isArray(prev?.rawData) && prev.rawData.length) || !!(Array.isArray(prev?.placements) && prev.placements.length);
        return has ? prev : buildDummyYoutubeData();
      });
      setVideoPreviews(prev => (Array.isArray(prev) && prev.length > 0) ? prev : buildDummyVideoPreviews());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build dummy raw rows for video performance
  const buildDummyVideoData = () => {
    const today = new Date();
    const days = 14;
    const out = [];
    
    const campaignTemplates = [
      { name: 'YouTube - Brand Awareness', budget: 15000, viewRate: 0.32 },
      { name: 'YouTube - Product Launch', budget: 12000, viewRate: 0.28 },
      { name: 'YouTube - Retargeting', budget: 8000, viewRate: 0.45 },
      { name: 'YouTube - Prospecting', budget: 10000, viewRate: 0.25 },
      { name: 'YouTube - Seasonal Campaign', budget: 18000, viewRate: 0.30 },
      { name: 'Video - In-Stream Ads', budget: 9000, viewRate: 0.35 },
      { name: 'Video - Discovery Ads', budget: 7000, viewRate: 0.38 },
      { name: 'YouTube - Competitor Conquest', budget: 6000, viewRate: 0.22 }
    ];
    
    const adGroupSuffixes = [
      { suffix: 'Broad Audience', factor: 0.4 },
      { suffix: 'Interest Based', factor: 0.35 },
      { suffix: 'Custom Intent', factor: 0.25 }
    ];
    
    campaignTemplates.forEach((campaign, campIdx) => {
      const campaignId = `VC-${campIdx + 1}`;
      
      adGroupSuffixes.forEach((ag, agIdx) => {
        const adGroupId = `VAG-${campIdx + 1}-${agIdx + 1}`;
        const adGroupName = `${campaign.name} - ${ag.suffix}`;
        
        for (let d = 0; d < days; d++) {
          const date = new Date(today);
          date.setDate(today.getDate() - (days - 1 - d));
          
          // Daily variance
          const dayVariance = 0.85 + Math.random() * 0.3;
          const weekendBoost = (date.getDay() === 0 || date.getDay() === 6) ? 1.15 : 1;
          
          const dailyBudget = (campaign.budget / 30) * ag.factor * dayVariance * weekendBoost;
          const cost = Math.round(dailyBudget);
          const cpm = 55 + Math.random() * 25;
          const impressions = Math.round((cost / cpm) * 1000);
          const videoViews = Math.round(impressions * campaign.viewRate * (0.9 + Math.random() * 0.2));
          const clicks = Math.round(videoViews * (0.04 + Math.random() * 0.03));
          const convRate = 0.02 + Math.random() * 0.03;
          const conversions = Math.round(clicks * convRate);
          const avgOrderValue = 850 + Math.random() * 400;
          const conversionValue = Math.round(conversions * avgOrderValue);
          
          // Quartile rates (realistic video completion rates)
          const q25 = 0.65 + Math.random() * 0.15;
          const q50 = q25 * (0.65 + Math.random() * 0.1);
          const q75 = q50 * (0.6 + Math.random() * 0.1);
          const q100 = q75 * (0.5 + Math.random() * 0.15);
          
          out.push({
            campaign_id: campaignId,
            campaign_name: campaign.name,
            ad_group_id: adGroupId,
            ad_group_name: adGroupName,
            date: date.toISOString().split('T')[0],
            cost,
            impressions,
            clicks,
            conversions,
            conversion_value: conversionValue,
            video_views: videoViews,
            video_quartile_p25_rate: q25,
            video_quartile_p50_rate: q50,
            video_quartile_p75_rate: q75,
            video_quartile_p100_rate: q100,
          });
        }
      });
    });
    
    return out;
  };

  // Build dummy YouTube placement/category dataset
  const buildDummyYoutubeData = () => {
    const categories = [
      { name: 'Fashion & Beauty', weight: 1.4 },
      { name: 'Entertainment', weight: 1.2 },
      { name: 'Technology', weight: 1.1 },
      { name: 'Gaming', weight: 0.9 },
      { name: 'News & Politics', weight: 0.7 },
      { name: 'Music', weight: 0.8 },
      { name: 'Sports', weight: 0.6 },
      { name: 'Education', weight: 0.5 }
    ];
    
    const channelData = [
      { name: 'Style Insider', cat: 'Fashion & Beauty' },
      { name: 'Tech Reviews Daily', cat: 'Technology' },
      { name: 'Gaming Universe', cat: 'Gaming' },
      { name: 'News Today', cat: 'News & Politics' },
      { name: 'Music Vibes', cat: 'Music' },
      { name: 'Fashion Forward', cat: 'Fashion & Beauty' },
      { name: 'Digital Trends', cat: 'Technology' },
      { name: 'Pro Gamer Hub', cat: 'Gaming' },
      { name: 'World Updates', cat: 'News & Politics' },
      { name: 'Hit Songs Channel', cat: 'Music' },
      { name: 'Beauty Tips Pro', cat: 'Fashion & Beauty' },
      { name: 'Gadget Zone', cat: 'Technology' },
      { name: 'Stream Masters', cat: 'Gaming' },
      { name: 'Breaking Stories', cat: 'News & Politics' },
      { name: 'Top Charts Music', cat: 'Music' },
      { name: 'Lifestyle Hub', cat: 'Fashion & Beauty' },
      { name: 'Innovation Lab', cat: 'Technology' },
      { name: 'eSports Arena', cat: 'Gaming' },
      { name: 'Daily Digest', cat: 'News & Politics' },
      { name: 'Melody Factory', cat: 'Music' },
      { name: 'Trend Spotters', cat: 'Fashion & Beauty' },
      { name: 'Future Tech', cat: 'Technology' },
      { name: 'Game Central', cat: 'Gaming' },
      { name: 'Sports Highlights', cat: 'Sports' },
      { name: 'Learn & Grow', cat: 'Education' },
      { name: 'Entertainment Weekly', cat: 'Entertainment' },
      { name: 'Movie Reviews', cat: 'Entertainment' },
      { name: 'Fitness Goals', cat: 'Sports' },
      { name: 'Study Smart', cat: 'Education' },
      { name: 'Pop Culture', cat: 'Entertainment' }
    ];
    
    const rawData = [];
    const channelNames = {};
    const today = new Date();
    
    // Generate more realistic data across 14 days
    for (let d = 0; d < 14; d++) {
      const date = new Date(today);
      date.setDate(today.getDate() - d);
      const dateStr = date.toISOString().split('T')[0];
      
      channelData.forEach((channel, idx) => {
        const channelId = `UC${(100000 + idx).toString(16)}${idx}abc`;
        const catInfo = categories.find(c => c.name === channel.cat) || { weight: 1 };
        
        const baseImpressions = 8000 + Math.random() * 12000;
        const impressions = Math.round(baseImpressions * catInfo.weight);
        const viewRate = 0.25 + Math.random() * 0.15;
        const views = Math.round(impressions * viewRate);
        const cpm = 45 + Math.random() * 35;
        const cost = Math.round((impressions / 1000) * cpm);
        const hasConversion = Math.random() > 0.7;
        const conversionValue = hasConversion ? Math.round(cost * (2.5 + Math.random() * 2)) : 0;
        
        rawData.push({
          placement: channelId,
          channel_title: channel.name,
          video_category_name: channel.cat,
          impressions,
          video_views: views,
          cost,
          date: dateStr,
          conversion_value: conversionValue,
          clicks: Math.round(views * 0.08),
          conversions: hasConversion ? Math.round(Math.random() * 5) + 1 : 0
        });
        
        if (!channelNames[channelId]) {
          channelNames[channelId] = channel.name;
        }
      });
    }
    
    // Calculate summary
    const totalImpressions = rawData.reduce((s, r) => s + r.impressions, 0);
    const totalViews = rawData.reduce((s, r) => s + r.video_views, 0);
    const uniquePlacements = new Set(rawData.map(r => r.placement)).size;
    
    const summary = {
      totalPlacements: uniquePlacements,
      avgVideoViewRate: totalImpressions > 0 ? ((totalViews / totalImpressions) * 100).toFixed(1) : 0,
    };
    
    return { rawData, channelNames, summary };
  };

  // Click-outside handler ekleyin
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDateDropdownOpen) {
        setIsDateDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      try { if (ytRetryTimer.current) clearTimeout(ytRetryTimer.current); } catch (_) {}
    };
  }, [isDateDropdownOpen]);

  // Kampanya genişletme/daraltma işlevi
  const toggleCampaign = (campaignId, e) => {
    e.stopPropagation();
    setExpandedCampaigns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(campaignId)) {
        newSet.delete(campaignId);
      } else {
        newSet.add(campaignId);
      }
      return newSet;
    });
  };

  // Reklam grubu genişletme/daraltma işlevi
  const toggleAdGroup = (adGroupId, e) => {
    e.stopPropagation();
    // Bu fonksiyon artık kullanılmıyor, çünkü reklam gruplarını direkt kampanya altında gösteriyoruz.
    // Ancak gelecekte lazım olabilir diye bırakıyorum.
    setExpandedAdGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(adGroupId)) {
        newSet.delete(adGroupId);
      } else {
        newSet.add(adGroupId);
      }
      return newSet;
    });
  };

  // Normalize campaign status to 'ENABLED' or 'PAUSED'
  const cleanStatus = (s) => {
    if (!s) return '';
    const cleaned = s.replace('CampaignStatus.', '').trim().toUpperCase();
    if (cleaned === 'ACTIVE' || cleaned === 'ENABLED') return 'ENABLED';
    if (cleaned === 'PAUSED') return 'PAUSED';
    return cleaned;
  };

  // Update campaign status or budget via live Google Ads API
  const updateEntity = useCallback(async (entityType, entityId, field, value, campaignId, oldValue, entityName) => {
    setUpdatingId(campaignId);
    setOpenStatusDropdown(null);
    try {
      const response = await fetch('https://api.glowytics.com/api/labs/google-ads/campaigns-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        body: JSON.stringify({ entityType, entityId, field, value, oldValue, entityName }),
        credentials: 'include'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.details?.error || 'Update failed');
      if (data.success) {
        toast.success('Updated successfully');
        // Optimistic update in liveDataMap
        setLiveDataMap(prev => {
          const key = String(campaignId);
          const existing = prev[key] || {};
          if (field === 'status') {
            return { ...prev, [key]: { ...existing, status: value } };
          } else if (field === 'amount_micros') {
            return { ...prev, [key]: { ...existing, budget: parseInt(value) / 1000000 } };
          }
          return prev;
        });
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdatingId(null);
      setEditBudget(null);
    }
  }, [selectedBrand?.id]);

  const handleSaveBudget = useCallback((campaignId, campaignName) => {
    if (!editBudget || editBudget.id !== campaignId) return;
    const newValue = parseFloat(editBudget.value);
    const micros = Math.round(newValue * 1000000);
    if (isNaN(micros) || micros <= 0) {
      toast.error('Invalid budget amount');
      return;
    }
    const live = liveDataMap[String(campaignId)];
    if (!live?.budgetId) {
      toast.error('Budget ID not found for this campaign');
      return;
    }
    const oldBudgetMicros = live.budget ? Math.round(live.budget * 1000000) : null;
    setConfirmDialog({
      title: 'Change Budget',
      message: `Are you sure you want to change the budget to ₺${newValue.toLocaleString('tr-TR')}?`,
      variant: 'budget',
      onConfirm: () => {
        updateEntity('budget', live.budgetId, 'amount_micros', micros, campaignId, oldBudgetMicros ? String(oldBudgetMicros) : null, campaignName || null);
        setConfirmDialog(null);
      }
    });
  }, [editBudget, liveDataMap, updateEntity]);

  // Fetch change history data
  const fetchChangeHistory = useCallback(async (offset = 0, append = false) => {
    if (!selectedBrand?.id) return;
    setChangeHistoryLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50', offset: String(offset) });
      if (changeHistoryFilter !== 'all') params.set('action', changeHistoryFilter);
      const response = await fetch(`https://api.glowytics.com/api/labs/ads-change-history?${params}`, {
        headers: { 'X-Brand-ID': selectedBrand.id },
        credentials: 'include'
      });
      const result = await response.json();
      if (result.success) {
        setChangeHistoryData(prev => append ? [...prev, ...result.data] : result.data);
        setChangeHistoryTotal(result.total);
      }
    } catch (err) {
      console.error('Failed to fetch change history:', err);
    } finally {
      setChangeHistoryLoading(false);
    }
  }, [selectedBrand?.id, changeHistoryFilter]);

  useEffect(() => {
    if (showChangeHistory) fetchChangeHistory(0);
  }, [showChangeHistory, fetchChangeHistory]);

  // Fetch live status + daily budget from Google Ads API
  const fetchLiveCampaignData = useCallback(async () => {
    if (!selectedBrand?.id) return;
    setIsLoadingLive(true);
    try {
      const response = await fetch('https://api.glowytics.com/api/labs/google-ads/campaigns-live', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        credentials: 'include'
      });
      const data = await response.json();
      console.log('[VideoCampaigns] campaigns-live response:', { ok: response.ok, success: data.success, count: data.campaigns?.length, sample: data.campaigns?.slice(0, 2) });
      if (response.ok && data.success && Array.isArray(data.campaigns)) {
        const map = {};
        data.campaigns.forEach(c => {
          if (c.id != null) {
            map[String(c.id)] = {
              id: c.id,
              status: c.status || null,
              budget: c.budget != null ? c.budget : null,
              budgetId: c.budgetId || null
            };
          }
        });
        console.log('[VideoCampaigns] liveDataMap keys:', Object.keys(map));
        setLiveDataMap(map);
      } else {
        console.warn('[VideoCampaigns] campaigns-live failed or empty:', { status: response.status, error: data.error });
      }
    } catch (e) {
      console.error('[VideoCampaigns] campaigns-live fetch error:', e);
    } finally {
      setIsLoadingLive(false);
    }
  }, [selectedBrand?.id]);

  // Fetch live data once on mount and whenever brand changes
  useEffect(() => {
    fetchLiveCampaignData();
  }, [fetchLiveCampaignData]);

  // Sıralama işlevi
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc'); // Yeni alana tıklandığında hep büyükten küçüğe sırala
    }
  };

  const handleMonthlySort = (field) => {
    if (monthlySortField === field) {
      setMonthlySortDirection(monthlySortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setMonthlySortField(field);
      setMonthlySortDirection('desc');
    }
  };

  // Video verilerini kampanya ve reklam gruplarına göre organize et
  const organizeVideoData = (data) => {
    if (!data || data.length === 0) {
      return { campaigns: [], dailyChartData: [] };
    }

    const campaignMap = new Map();
    const dailyDataMap = new Map();

    data.forEach(item => {
      const campaignId = item.campaign_id;

      // Günlük veriyi haritaya ekle (grafik için)
      const date = new Date(item.date).toISOString().split('T')[0];
      if (!dailyDataMap.has(date)) {
        dailyDataMap.set(date, { date, cost: 0, video_views: 0 });
      }
      const dayData = dailyDataMap.get(date);
      dayData.cost += Number(item.cost || 0);
      dayData.video_views += Number(item.video_views || 0);

      // Kampanyaları ve reklam gruplarını topla
      if (!campaignMap.has(campaignId)) {
        campaignMap.set(campaignId, {
          id: campaignId,
          name: item.campaign_name,
          status: 'ENABLED', // Varsayılan, API'den gelmiyorsa
          cost: 0,
          impressions: 0,
          clicks: 0,
          conversions: 0,
          conversion_value: 0,
          video_views: 0,
          total_view_rate_impressions: 0, // Oran hesaplaması için
          total_quartile_p25_views: 0,
          total_quartile_p50_views: 0,
          total_quartile_p75_views: 0,
          total_quartile_p100_views: 0,
          adGroups: new Map()
        });
      }

      const campaign = campaignMap.get(campaignId);
      campaign.cost += Number(item.cost || 0);
      campaign.impressions += Number(item.impressions || 0);
      campaign.clicks += Number(item.clicks || 0);
      campaign.conversions += Number(item.conversions || 0);
      campaign.conversion_value += Number(item.conversion_value || 0);
      campaign.video_views += Number(item.video_views || 0);
      
      // Oran hesaplamaları için
      campaign.total_view_rate_impressions += Number(item.impressions || 0);
      campaign.total_quartile_p25_views += Number(item.video_views || 0) * Number(item.video_quartile_p25_rate || 0);
      campaign.total_quartile_p50_views += Number(item.video_views || 0) * Number(item.video_quartile_p50_rate || 0);
      campaign.total_quartile_p75_views += Number(item.video_views || 0) * Number(item.video_quartile_p75_rate || 0);
      campaign.total_quartile_p100_views += Number(item.video_views || 0) * Number(item.video_quartile_p100_rate || 0);

      const adGroupMap = campaign.adGroups;
      const adGroupId = item.ad_group_id;
      if (!adGroupMap.has(adGroupId)) {
        adGroupMap.set(adGroupId, {
          id: adGroupId,
          name: item.ad_group_name,
          status: 'Active',
          cost: 0,
          impressions: 0,
          clicks: 0,
          conversions: 0,
          conversion_value: 0,
          video_views: 0,
          total_view_rate_impressions: 0,
          total_quartile_p25_views: 0,
          total_quartile_p50_views: 0,
          total_quartile_p75_views: 0,
          total_quartile_p100_views: 0,
        });
      }
      
      const adGroup = adGroupMap.get(adGroupId);
      adGroup.cost += Number(item.cost || 0);
      adGroup.impressions += Number(item.impressions || 0);
      adGroup.clicks += Number(item.clicks || 0);
      adGroup.conversions += Number(item.conversions || 0);
      adGroup.conversion_value += Number(item.conversion_value || 0);
      adGroup.video_views += Number(item.video_views || 0);
      adGroup.total_view_rate_impressions += Number(item.impressions || 0);
      adGroup.total_quartile_p25_views += Number(item.video_views || 0) * Number(item.video_quartile_p25_rate || 0);
      adGroup.total_quartile_p50_views += Number(item.video_views || 0) * Number(item.video_quartile_p50_rate || 0);
      adGroup.total_quartile_p75_views += Number(item.video_views || 0) * Number(item.video_quartile_p75_rate || 0);
      adGroup.total_quartile_p100_views += Number(item.video_views || 0) * Number(item.video_quartile_p100_rate || 0);
    });

    const dailyChartData = Array.from(dailyDataMap.values()).sort((a,b) => new Date(a.date) - new Date(b.date));

    const campaigns = Array.from(campaignMap.values()).map(campaign => {
      campaign.adGroups = Array.from(campaign.adGroups.values()).map(adGroup => {
        adGroup.video_view_rate = adGroup.total_view_rate_impressions > 0 ? (adGroup.video_views / adGroup.total_view_rate_impressions) * 100 : 0;
        adGroup.cost_per_view = adGroup.video_views > 0 ? adGroup.cost / adGroup.video_views : 0;
        adGroup.video_quartile_p25_rate = adGroup.video_views > 0 ? (adGroup.total_quartile_p25_views / adGroup.video_views) * 100 : 0;
        adGroup.video_quartile_p50_rate = adGroup.video_views > 0 ? (adGroup.total_quartile_p50_views / adGroup.video_views) * 100 : 0;
        adGroup.video_quartile_p75_rate = adGroup.video_views > 0 ? (adGroup.total_quartile_p75_views / adGroup.video_views) * 100 : 0;
        adGroup.video_quartile_p100_rate = adGroup.video_views > 0 ? (adGroup.total_quartile_p100_views / adGroup.video_views) * 100 : 0;
        return adGroup;
      });

      campaign.video_view_rate = campaign.total_view_rate_impressions > 0 ? (campaign.video_views / campaign.total_view_rate_impressions) * 100 : 0;
      campaign.cost_per_view = campaign.video_views > 0 ? campaign.cost / campaign.video_views : 0;
      campaign.video_quartile_p25_rate = campaign.video_views > 0 ? (campaign.total_quartile_p25_views / campaign.video_views) * 100 : 0;
      campaign.video_quartile_p50_rate = campaign.video_views > 0 ? (campaign.total_quartile_p50_views / campaign.video_views) * 100 : 0;
      campaign.video_quartile_p75_rate = campaign.video_views > 0 ? (campaign.total_quartile_p75_views / campaign.video_views) * 100 : 0;
      campaign.video_quartile_p100_rate = campaign.video_views > 0 ? (campaign.total_quartile_p100_views / campaign.video_views) * 100 : 0;
      
      return campaign;
    });

    // Demo brand: manipulate campaign and ad group names slightly
    try {
      const currentBrandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
      if (currentBrandId === DEMO_BRAND_ID_SC) {
        const suffixes = [' | Ultra', ' | Flex', ' | Pro', ' | Max', ' | Elite'];
        campaigns.forEach((c, idx) => {
          const sfx = suffixes[idx % suffixes.length];
          c.name = `YouTube ${c.name}${sfx}`;
          c.adGroups = (c.adGroups || []).map((ag, j) => ({
            ...ag,
            name: `${ag.name} ${['A','B','C','D'][j % 4]}`
          }));
        });
      }
    } catch (_) {}

    console.log('[VideoCampaigns] campaign IDs from DB:', campaigns.map(c => ({ id: c.id, type: typeof c.id, name: c.name })));
    return { campaigns, dailyChartData };
  };

  // Aggregate video data by month for Monthly Breakdown table
  const aggregateMonthlyData = (data) => {
    if (!data || data.length === 0) return [];

    const monthMap = new Map();

    data.forEach(item => {
      const dateStr = item.date ? new Date(item.date).toISOString().slice(0, 7) : null; // YYYY-MM
      if (!dateStr) return;

      if (!monthMap.has(dateStr)) {
        monthMap.set(dateStr, {
          month: dateStr,
          cost: 0,
          impressions: 0,
          clicks: 0,
          conversions: 0,
          conversion_value: 0,
          video_views: 0,
          total_quartile_p25_views: 0,
          total_quartile_p50_views: 0,
          total_quartile_p75_views: 0,
          total_quartile_p100_views: 0,
        });
      }

      const m = monthMap.get(dateStr);
      const views = Number(item.video_views || 0);
      m.cost += Number(item.cost || 0);
      m.impressions += Number(item.impressions || 0);
      m.clicks += Number(item.clicks || 0);
      m.conversions += Number(item.conversions || 0);
      m.conversion_value += Number(item.conversion_value || 0);
      m.video_views += views;
      m.total_quartile_p25_views += views * Number(item.video_quartile_p25_rate || 0);
      m.total_quartile_p50_views += views * Number(item.video_quartile_p50_rate || 0);
      m.total_quartile_p75_views += views * Number(item.video_quartile_p75_rate || 0);
      m.total_quartile_p100_views += views * Number(item.video_quartile_p100_rate || 0);
    });

    return Array.from(monthMap.values()).map(m => ({
      ...m,
      cpm: m.impressions > 0 ? (m.cost / m.impressions) * 1000 : 0,
      view_rate: m.impressions > 0 ? (m.video_views / m.impressions) * 100 : 0,
      cost_per_view: m.video_views > 0 ? m.cost / m.video_views : 0,
      video_quartile_p25_rate: m.video_views > 0 ? (m.total_quartile_p25_views / m.video_views) * 100 : 0,
      video_quartile_p50_rate: m.video_views > 0 ? (m.total_quartile_p50_views / m.video_views) * 100 : 0,
      video_quartile_p75_rate: m.video_views > 0 ? (m.total_quartile_p75_views / m.video_views) * 100 : 0,
      video_quartile_p100_rate: m.video_views > 0 ? (m.total_quartile_p100_views / m.video_views) * 100 : 0,
    })).sort((a, b) => b.month.localeCompare(a.month)); // newest first
  };

  // Calculate summary metrics from campaigns
  const calculateSummaryMetrics = (campaigns) => {
    if (!campaigns || campaigns.length === 0) {
      return {
        cpc: 0,
        conversionRate: 0,
        cost: 0,
        roas: 0,
        total_video_views: 0,
        avg_view_rate: 0,
        avg_cost_per_view: 0,
      };
    }

    let totalClicks = 0;
    let totalCost = 0;
    let totalConversions = 0;
    let totalRevenue = 0;
    let totalVideoViews = 0;
    let totalImpressions = 0;

    campaigns.forEach(campaign => {
      totalClicks += campaign.clicks || 0;
      totalCost += campaign.cost || 0;
      totalConversions += campaign.conversions || 0;
      totalRevenue += campaign.conversion_value || 0;
      totalVideoViews += campaign.video_views || 0;
      totalImpressions += campaign.impressions || 0;
    });

    const avgCpc = totalClicks > 0 ? totalCost / totalClicks : 0;
    const conversionRate = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;
    const roas = totalCost > 0 ? totalRevenue / totalCost : 0;
    const avgViewRate = totalImpressions > 0 ? (totalVideoViews / totalImpressions) * 100 : 0;
    const avgCostPerView = totalVideoViews > 0 ? totalCost / totalVideoViews : 0;

    return {
      cpc: avgCpc,
      conversionRate: conversionRate,
      cost: totalCost,
      roas: roas,
      total_video_views: totalVideoViews,
      avg_view_rate: avgViewRate,
      avg_cost_per_view: avgCostPerView,
    };
  };

  useEffect(() => {
    // Fire all fetches independently so fast ones render immediately
    setDataLoading(true);
    fetchVideoData()
      .catch(error => {
        console.error('Error fetching video data:', error);
        toast.error('Failed to fetch video campaign data');
      })
      .finally(() => setDataLoading(false));

    // YouTube placements & video previews load independently with their own loading states
    const shouldFetchYT = dateRange !== 'custom' || (customStartDate && customEndDate);
    if (shouldFetchYT) fetchYoutubeData();
    fetchVideoPreviews();
  }, [dateRange, customStartDate, customEndDate, selectedAccountId, gaAccountFilter]);

  useEffect(() => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return;
    fetch('https://api.glowytics.com/api/auth/connections', {
      credentials: 'include',
      headers: { 'X-Brand-ID': brandId }
    })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.connections || []);
        const ga = list.filter(c => c.platform === 'GOOGLE_ADS' && c.status === 'ACTIVE');
        setGaAccounts(ga);
      })
      .catch(() => {});
  }, []);

  // Process video data when it changes
  useEffect(() => {
    if (videoData && Array.isArray(videoData)) {
      try {
        const { campaigns: fetchedCampaigns, dailyChartData } = organizeVideoData(videoData);
        setCampaigns(fetchedCampaigns);
        setMonthlyData(aggregateMonthlyData(videoData));
        setExpandedCampaigns(new Set()); // Start with all collapsed
        
        // Update chart
        if (dailyChartData && dailyChartData.length > 0) {
          const labels = dailyChartData.map(d => new Date(d.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }));
          const videoViewsData = dailyChartData.map(d => d.video_views);
          const costData = dailyChartData.map(d => d.cost);
          setChartData(prev => ({
            ...prev,
            labels: labels,
            datasets: [
              { ...prev.datasets[0], data: videoViewsData },
              { ...prev.datasets[1], data: costData },
            ]
          }));
        } else if (dateRange === 'custom') {
          // When custom date has no data, do NOT fallback to last30 days; show empty chart
          setChartData(prev => ({
            ...prev,
            labels: [],
            datasets: [
              { ...prev.datasets[0], data: [] },
              { ...prev.datasets[1], data: [] },
            ]
          }));
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error processing video data:', error);
        setIsLoading(false);
      }
    }
  }, [videoData]);

  // Function to fetch video data from the database
  const fetchVideoData = async () => {
    try {
      setIsLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('No brand selected.');
        setIsLoading(false);
        return;
      }

      let url = `https://api.glowytics.com/api/google-ads/video-performance-summary?dateRange=${dateRange}${gaAccountFilter !== 'all' ? '&accountId=' + gaAccountFilter : ''}`;
      // Local YYYY-MM-DD formatter (avoid timezone shift from toISOString)
      const toLocalYMD = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        url += `&startDate=${toLocalYMD(customStartDate)}&endDate=${toLocalYMD(customEndDate)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to fetch video data: ${errorData.error || response.statusText}`);
      }

      let data = await response.json();
      // Bu sayfa YALNIZCA YouTube (VIDEO) kampanyalarına ait. Endpoint 2026-07-28'den
      // beri PMax / Demand Gen / Display izlenme satirlarini da donduruyor (toplam
      // izlenme raporlarda dogru ciksin diye) — onlarin ad group'u yok ve buraya
      // girerlerse hem listeyi kirletir hem harcamayi sisirirler.
      if (Array.isArray(data)) {
        data = data.filter(r => String(r.campaign_type ?? 'VIDEO').toUpperCase() === 'VIDEO');
      }
      if (isDemoBrandId(brandId) && (!Array.isArray(data) || data.length === 0)) {
        data = buildDummyVideoData();
      }
      // Bumper / non-skippable / CTV: TrueView view üretmez (view=0) ama izlenme
      // sayılmalı → view yoksa impression'ı izlenme (video_views) say. Backend
      // effective_views alanını kullan; yoksa aynı kuralı client-side uygula.
      // Tek noktada normalize → tüm alt-toplamlar (günlük/kampanya/adgroup/total) tutarlı.
      if (Array.isArray(data)) {
        data = data.map((r) => {
          const vv = Number(r.video_views) || 0;
          const ev = r.effective_views != null
            ? Number(r.effective_views)
            : (vv > 0 ? vv : Number(r.impressions) || 0);
          return { ...r, video_views: ev };
        });
      }
      setVideoData(data);
      
    } catch (error) {
      console.error('Error fetching video data:', error);
      toast.error(`Failed to fetch video data: ${error.message}`);
      setIsLoading(false);
    }
  };

  // Function to fetch YouTube placement data
  const fetchYoutubeData = async () => {
    setIsYoutubeLoading(true);
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.log('No brand ID found for YouTube data');
        setIsYoutubeLoading(false);
        return;
      }

      // Kanal isimlerini de çekmek için fetchChannelNames parametresi ekle
      let url = `https://api.glowytics.com/api/google-ads/youtube-placement-raw?dateRange=${dateRange}&fetchChannelNames=true${gaAccountFilter !== 'all' ? '&accountId=' + gaAccountFilter : ''}`;
      const toLocalYMD = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        const s = toLocalYMD(customStartDate);
        const e = toLocalYMD(customEndDate);
        url += `&startDate=${s}&endDate=${e}`;
      }
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to fetch YouTube data:', errorData);
        setIsYoutubeLoading(false);
        return;
      }

      let data = await response.json();
      const rowsIn = (d) => !!(Array.isArray(d?.rawData) && d.rawData.length) ||
                            !!(Array.isArray(d?.placements) && d.placements.length) ||
                            !!(Array.isArray(d?.topPlacements) && d.topPlacements.length);
      // Demo brands: substitute static data whenever the API has no real rows
      // (covers empty arrays, not just a missing key — otherwise the retry loop
      // below spins forever on `{ rawData: [] }`).
      if (isDemoBrandId(brandId) && !rowsIn(data)) {
        data = buildDummyYoutubeData();
      }
      // If response has no placement rows yet (backend still filling), briefly poll a few times (max 2 retries = 8 sec total)
      const hasRows = rowsIn(data);
      if (!hasRows && ytRetryCount < 2) {
        try { if (ytRetryTimer.current) clearTimeout(ytRetryTimer.current); } catch (_) {}
        ytRetryTimer.current = setTimeout(() => {
          setYtRetryCount((c) => c + 1);
          fetchYoutubeData();
        }, 4000);
        // keep loading state true; do not set data yet
        return;
      }
      // After max retries or if we have data, always stop loading and set data (even if empty)
      try { if (ytRetryTimer.current) clearTimeout(ytRetryTimer.current); } catch (_) {}
      setYoutubeData(data || null);
      setYtRetryCount(0);
      setIsYoutubeLoading(false);
    } catch (error) {
      console.error('Error fetching YouTube data:', error);
      // Always stop loading on error
      try { if (ytRetryTimer.current) clearTimeout(ytRetryTimer.current); } catch (_) {}
      setYtRetryCount(0);
      setIsYoutubeLoading(false);
    }
  };

  // Fetch active YouTube campaign video previews (live API + DB fallbacks)
  const fetchVideoPreviews = async () => {
    setVideoPreviewsLoading(true);
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        setVideoPreviews([]);
        setVideoPreviewsLoading(false);
        return;
      }
      const toLocalYMD = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      const headers = {
        'Content-Type': 'application/json',
        'X-Brand-ID': brandId,
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      };
      const is11CharVideoId = (v) => v && typeof v === 'string' && /^[A-Za-z0-9_-]{11}$/.test(v);

      // 1) Try youtube-campaigns-live (Google Ads API, no DB)
      let items = [];
      try {
        let url = `https://api.glowytics.com/api/google-ads/youtube-campaigns-live?dateRange=${dateRange}${gaAccountFilter !== 'all' ? '&accountId=' + gaAccountFilter : ''}`;
        if (dateRange === 'custom' && customStartDate && customEndDate) {
          url += `&startDate=${toLocalYMD(customStartDate)}&endDate=${toLocalYMD(customEndDate)}`;
        }
        const res = await fetch(url, { method: 'GET', headers, credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const seen = new Set();
          if (data?.success && Array.isArray(data.campaigns)) {
            for (const c of data.campaigns) {
              for (const vid of (c.videoIds || [])) {
                if (is11CharVideoId(vid) && !seen.has(vid)) {
                  seen.add(vid);
                  items.push({ videoId: vid, campaignName: c.campaignName || c.campaignId || '' });
                  if (items.length >= 24) break;
                }
              }
              if (items.length >= 24) break;
            }
          }
        }
      } catch (_) {}

      // 2) Fallback: youtube-campaigns-preview (asset table, primaryVideoId per campaign)
      if (items.length === 0) {
        try {
          let url = `https://api.glowytics.com/api/google-ads/youtube-campaigns-preview?dateRange=${dateRange}${gaAccountFilter !== 'all' ? '&accountId=' + gaAccountFilter : ''}`;
          if (dateRange === 'custom' && customStartDate && customEndDate) {
            url += `&startDate=${toLocalYMD(customStartDate)}&endDate=${toLocalYMD(customEndDate)}`;
          }
          const res = await fetch(url, { method: 'GET', headers, credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            const seen = new Set();
            for (const c of data?.campaigns || []) {
              const vid = c.primaryVideoId || c.primary_video_id;
              if (is11CharVideoId(vid) && !seen.has(vid)) {
                seen.add(vid);
                items.push({ videoId: vid, campaignName: c.campaign_name || c.campaignName || '' });
                if (items.length >= 24) break;
              }
            }
          }
        } catch (_) {}
      }

      // 3) Fallback: youtube-placement-raw (video_id from placement rows)
      if (items.length === 0) {
        try {
          let url = `https://api.glowytics.com/api/google-ads/youtube-placement-raw?dateRange=${dateRange}&fetchChannelNames=false&topPlacementsOnly=false${gaAccountFilter !== 'all' ? '&accountId=' + gaAccountFilter : ''}`;
          if (dateRange === 'custom' && customStartDate && customEndDate) {
            url += `&startDate=${toLocalYMD(customStartDate)}&endDate=${toLocalYMD(customEndDate)}`;
          }
          const res = await fetch(url, { method: 'GET', headers, credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            const seen = new Set();
            const rows = data?.rawData || data?.placements || [];
            for (const r of rows) {
              const vid = r.video_id || (r.placement && /^[A-Za-z0-9_-]{11}$/.test(r.placement) ? r.placement : null);
              const name = r.campaign_name || r.campaignName || '';
              if (is11CharVideoId(vid) && !seen.has(vid)) {
                seen.add(vid);
                items.push({ videoId: vid, campaignName: name });
                if (items.length >= 24) break;
              }
            }
          }
        } catch (_) {}
      }

      // Demo brands: fall back to static previews when the API yields nothing.
      if (items.length === 0 && isDemoBrandId(brandId)) {
        items = buildDummyVideoPreviews();
      }

      setVideoPreviews(items);
    } catch (err) {
      console.error('Error fetching video previews:', err);
      const brandId = localStorage.getItem('currentBrandId');
      setVideoPreviews(isDemoBrandId(brandId) ? buildDummyVideoPreviews() : []);
    } finally {
      setVideoPreviewsLoading(false);
    }
  };

  // Guarantee dummy on client if API delivered empty
  useEffect(() => {
    const brandId = localStorage.getItem('currentBrandId');
    if (isDemoBrandId(brandId)) {
      if (!Array.isArray(videoData) || videoData.length === 0) {
        setVideoData(buildDummyVideoData());
      }
      const ytHasRows = !!(Array.isArray(youtubeData?.rawData) && youtubeData.rawData.length) ||
                        !!(Array.isArray(youtubeData?.placements) && youtubeData.placements.length) ||
                        !!(Array.isArray(youtubeData?.topPlacements) && youtubeData.topPlacements.length);
      if (!ytHasRows) {
        setYoutubeData(buildDummyYoutubeData());
      }
      if (!Array.isArray(videoPreviews) || videoPreviews.length === 0) {
        setVideoPreviews(buildDummyVideoPreviews());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, videoData, youtubeData, videoPreviews]);

  // Clear any scheduled polls on unmount or when dateRange changes
  useEffect(() => {
    return () => {
      try { if (ytRetryTimer.current) clearTimeout(ytRetryTimer.current); } catch (_) {}
    };
  }, [dateRange]);

  // Update the calculateDateRange function to handle the "This Month" filter
  const calculateDateRange = (selectedRange) => {
    const now = new Date();
    let startDate, endDate;

    switch (selectedRange) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'yesterday':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
        break;
      case '7days':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
        break;
      case '30days':
        // Fix: Correctly set to 30 days ago instead of 7
        
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        console.log(`30 days range: ${startDate.toISOString()} to ${endDate.toISOString()}`);
        break;
      case 'thisMonth':
        // First day of current month
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        // Current day
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'lastMonth':
        // First day of last month
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        // Last day of last month
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  // Helper function to filter data by date range
  const filterDataByDateRange = (data, dateRange) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    // Set time to end of day for end date for inclusive comparison
    endDate.setHours(23, 59, 59, 999);
    
    return data.filter(item => {
      if (!item.date) return false;
      
      const itemDate = new Date(item.date);
      return itemDate >= startDate && itemDate <= endDate;
    });
  };

  // New function to group keyword data by date and calculate total cost
  // This function is no longer needed as we're processing video data differently
  
  // New function to update the chart with daily cost data
  // This logic is now inside the useEffect that processes videoData
  
  // Performans trend verilerini çekmek için yeni fonksiyon
  // Bu fonksiyon da artık gerekli değil, grafik verisi ana veriyle birlikte oluşturuluyor.
  
  // Keyword verilerinden trend verilerini oluşturan yardımcı fonksiyon
  // Bu da artık gereksiz.
  
  // Boş trend verisi oluşturan yardımcı fonksiyon
  const generateEmptyTrendData = () => {
    console.log("Generating empty trend data for the last 30 days");
    
    // Son 30 günün tarihlerini oluştur
    const labels = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(today.getDate() - i);
      // Tarihi gün-ay formatında göster
      labels.push(date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }));
    }
    
    return {
      labels: labels,
      datasets: [
        {
          label: 'Spend',
          data: Array(30).fill(0),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y'
        },
        {
          label: 'Revenue',
          data: Array(30).fill(0),
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y'
        }
      ]
    };
  };

  // Find and update the formatNumber function or any other function that formats currency values
  const formatNumber = (value, options = {}) => {
    if (value === undefined || value === null || isNaN(value)) return '0';
    
    const {
      prefix = '',
      suffix = '',
      minimumFractionDigits,
      maximumFractionDigits,
      notation = 'standard'
    } = options;

    const finalOptions = {
      minimumFractionDigits: minimumFractionDigits ?? 2,
      maximumFractionDigits: maximumFractionDigits ?? 2,
      notation,
    };

    if (maximumFractionDigits !== undefined) {
      finalOptions.minimumFractionDigits = minimumFractionDigits ?? Math.min(2, maximumFractionDigits);
    }
    
    // Remove any existing currency symbol from the value if it's a string
    let numericValue = value;
    if (typeof value === 'string') {
      numericValue = value.replace(/[₺$€£¥]/g, '');
    }
    
    // Convert to number
    numericValue = parseFloat(numericValue);
    
    // Format the number
    return `${prefix}${numericValue.toLocaleString(undefined, finalOptions)}${suffix}`;
  };

  // Lightweight loading skeleton for lists (categories/placements)
  const LoadingSkeleton = ({ lines = 6 }) => (
    <div className="w-full">
      <div className="animate-pulse space-y-3">
        {Array.from({ length: lines }).map((_, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <div className="w-6 h-3 bg-gray-200 rounded" />
            <div className="flex-1">
              <div className="h-3 bg-gray-200 rounded w-3/5" />
              <div className="h-2 bg-gray-100 rounded w-full mt-2" />
            </div>
            <div className="h-3 bg-gray-200 rounded w-16" />
          </div>
        ))}
      </div>
    </div>
  );

  // Get summary metrics
  const summaryMetrics = calculateSummaryMetrics(campaigns);

  // useEffect içinde, sayfa yüklendiğinde varsayılan olarak budget'a göre sırala
  useEffect(() => {
    setSortField('cost');
    setSortDirection('desc');
  }, []);

  // Kampanyaları sırala
  const sortedCampaigns = [...campaigns].sort((a, b) => {
    const field = sortField;
    const direction = sortDirection;

    // Sayısal değerler için sıralama
    if (['spend', 'cost', 'impressions', 'clicks', 'video_views', 'conversions', 'conversion_value', 'video_view_rate', 'cost_per_view', 'cpm'].includes(field)) {
      const aValue = field === 'cpm'
        ? ((a.impressions || 0) > 0 ? (a.cost / a.impressions) * 1000 : 0)
        : (parseFloat(a[field]) || 0);
      const bValue = field === 'cpm'
        ? ((b.impressions || 0) > 0 ? (b.cost / b.impressions) * 1000 : 0)
        : (parseFloat(b[field]) || 0);
      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    // Metin değerleri için sıralama (kampanya adı, durum vb.)
    if (['name', 'status'].includes(field)) {
      const aValue = String(a[field] || '').toLowerCase();
      const bValue = String(b[field] || '').toLowerCase();
      return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    // Varsayılan olarak cost'a göre azalan sıralama
    return b.cost - a.cost;
  });

  // Ad Group'ları sırala
  const sortAdGroups = (adGroups, field, direction) => {
    if (!field) return adGroups;

    return [...adGroups].sort((a, b) => {
      // Sayısal değerler için sıralama
      if (['spend', 'cost', 'impressions', 'clicks', 'video_views', 'conversions', 'conversion_value', 'video_view_rate', 'cost_per_view', 'cpm'].includes(field)) {
        const aValue = field === 'cpm'
          ? ((a.impressions || 0) > 0 ? (a.cost / a.impressions) * 1000 : 0)
          : (parseFloat(a[field]) || 0);
        const bValue = field === 'cpm'
          ? ((b.impressions || 0) > 0 ? (b.cost / b.impressions) * 1000 : 0)
          : (parseFloat(b[field]) || 0);

        return direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }
      
      // Metin değerleri için sıralama (ad group adı, durum vb.)
      if (['name', 'status'].includes(field)) {
        const aValue = String(a[field] || '').toLowerCase();
        const bValue = String(b[field] || '').toLowerCase();
        
        if (direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
      
      // Varsayılan olarak cost'a göre azalan sıralama
      return b.cost - a.cost;
    });
  };

  // Sorted monthly data
  const sortedMonthlyData = [...monthlyData].sort((a, b) => {
    const f = monthlySortField;
    const d = monthlySortDirection;
    if (f === 'month') {
      return d === 'asc' ? a.month.localeCompare(b.month) : b.month.localeCompare(a.month);
    }
    const aVal = parseFloat(a[f]) || 0;
    const bVal = parseFloat(b[f]) || 0;
    return d === 'asc' ? aVal - bVal : bVal - aVal;
  });

  // Grand total for monthly breakdown
  const monthlyGrandTotal = monthlyData.reduce((acc, m) => {
    acc.cost += m.cost;
    acc.impressions += m.impressions;
    acc.clicks += m.clicks;
    acc.conversions += m.conversions;
    acc.conversion_value += m.conversion_value;
    acc.video_views += m.video_views;
    acc.total_q25 += m.total_quartile_p25_views || 0;
    acc.total_q50 += m.total_quartile_p50_views || 0;
    acc.total_q75 += m.total_quartile_p75_views || 0;
    acc.total_q100 += m.total_quartile_p100_views || 0;
    return acc;
  }, { cost: 0, impressions: 0, clicks: 0, conversions: 0, conversion_value: 0, video_views: 0, total_q25: 0, total_q50: 0, total_q75: 0, total_q100: 0 });

  // Format month key (YYYY-MM) to display label
  const formatMonthLabel = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="p-6 space-y-6 bg-[#f6f8ff]/70">
      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmDialog(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className={`px-6 pt-6 pb-4 flex items-start gap-4 ${
              confirmDialog.variant === 'pause' ? 'bg-orange-50/50' :
              confirmDialog.variant === 'enable' ? 'bg-green-50/50' :
              'bg-blue-50/50'
            }`}>
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                confirmDialog.variant === 'pause' ? 'bg-orange-100' :
                confirmDialog.variant === 'enable' ? 'bg-green-100' :
                'bg-blue-100'
              }`}>
                <AlertTriangle className={`w-5 h-5 ${
                  confirmDialog.variant === 'pause' ? 'text-orange-600' :
                  confirmDialog.variant === 'enable' ? 'text-green-600' :
                  'text-blue-600'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{confirmDialog.title}</h3>
                <p className="mt-1 text-sm text-gray-600">{confirmDialog.message}</p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50/80 flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmDialog(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors focus:outline-none focus:ring-2 ${
                  confirmDialog.variant === 'pause' ? 'bg-orange-500 hover:bg-orange-600 focus:ring-orange-200' :
                  confirmDialog.variant === 'enable' ? 'bg-green-500 hover:bg-green-600 focus:ring-green-200' :
                  'bg-blue-500 hover:bg-blue-600 focus:ring-blue-200'
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center bg-white px-5 py-3 rounded-2xl shadow-sm w-full gap-3 md:gap-0 min-w-0">
        <div className="flex items-center gap-2.5 w-full md:w-auto">
          <img
            src="/video.svg"
            alt="Video Campaigns"
            className="w-7 h-7 object-contain"
          />
          <h1 className="text-lg font-semibold text-gray-900">Video Campaigns</h1>
        </div>

        <div className="flex flex-wrap items-center gap-3 justify-center md:justify-end w-full md:w-auto">
          {/* Account Switcher */}
          <AccountSwitcher platform="GOOGLE_ADS" />

          {/* Seçili tarih aralığı */}
          <div className="text-xs text-gray-500">
            {(() => {
              switch (dateRange) {
                case 'today':
                  return new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                case 'yesterday':
                  const yesterday = new Date();
                  yesterday.setDate(yesterday.getDate() - 1);
                  return yesterday.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                case 'last7':
                  const last7 = new Date();
                  last7.setDate(last7.getDate() - 7);
                  return `${last7.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'last30':
                  const last30 = new Date();
                  last30.setDate(last30.getDate() - 30);
                  return `${last30.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'thisMonth':
                  const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
                  return `${firstDay.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'lastMonth':
                  const lastMonthFirstDay = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
                  const lastMonthLastDay = new Date(new Date().getFullYear(), new Date().getMonth(), 0);
                  return `${lastMonthFirstDay.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${lastMonthLastDay.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'custom':
                  if (customStartDate && customEndDate) {
                    return `${customStartDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${customEndDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                  }
                  return 'Custom Range';
                default:
                  return '';
              }
            })()}
          </div>

          {/* Refresh button */}
          <button 
            onClick={() => { fetchVideoData(); fetchVideoPreviews(); }}
            className={`p-2 text-gray-600 hover:text-blue-600 rounded-xl hover:bg-blue-50 ${isLoading ? 'animate-spin' : ''}`}
          >
            <RefreshCw className="w-5 h-5" />
          </button>

          {/* Google Ads Account Filter */}
          {gaAccounts.length >= 2 && (
            <select
              value={gaAccountFilter}
              onChange={(e) => setGaAccountFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-xl text-sm bg-white shadow-sm hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              <option value="all">All Accounts</option>
              {gaAccounts.map(acc => (
                <option key={acc.customerId} value={acc.customerId}>
                  {acc.nickname || acc.accountName || acc.customerId}
                </option>
              ))}
            </select>
          )}

          {/* Unified date range filter (replaces legacy dropdown + modal) */}
          <DateRangeFilter
            value={dateRange}
            customStart={customStartDate}
            customEnd={customEndDate}
            onChange={(range, start, end) => {
              setDateRange(range);
              setCustomStartDate(start);
              setCustomEndDate(end);
            }}
            presets={['yesterday', 'last7', 'last30', 'thisMonth', 'lastMonth', 'custom']}
          />

        </div>
      </div>

      <Ga4WebOnlyNote />

      {/* Performance Overview */}
      <div className="grid grid-cols-12 gap-6">
        {/* Chart */}
        <Card className="col-span-8 rounded-2xl overflow-hidden bg-white">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Video Ad Performance</h2>
                <p className="text-sm text-gray-500 mt-1">YouTube and video ads performance metrics</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="h-[300px]">
              {isLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="loading-logo-container">
                    <img 
                      src="/logoicon.png" 
                      alt="Glowytics Logo" 
                      className="loading-logo"
                    />
                  </div>
                </div>
              ) : (
                <Line data={chartData} options={{...chartOptions, scales: {...chartOptions.scales, y2: { position: 'right', grid: { drawOnChartArea: false } }}}} />
              )}
            </div>
          </div>
        </Card>

        {/* Key Metrics */}
        <div className="col-span-4 grid grid-rows-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm p-4 col-span-1 row-span-1">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Total Video Views</p>
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Video className="w-4 h-4 text-blue-600" />
                </div>
              </div>
              <div className="mt-1">
                <h3 className="text-2xl font-semibold text-gray-900">{formatNumber(summaryMetrics.total_video_views, {maximumFractionDigits: 0})}</h3>
              </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 col-span-1 row-span-1">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Avg. View Rate</p>
                <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-purple-600" />
                </div>
              </div>
              <div className="mt-1">
                <h3 className="text-2xl font-semibold text-gray-900">{formatNumber(summaryMetrics.avg_view_rate)}%</h3>
              </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 col-span-1 row-span-1">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Total Cost</p>
                <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-red-600" />
                </div>
              </div>
              <div className="mt-1">
                <h3 className="text-2xl font-semibold text-gray-900">₺{formatNumber(summaryMetrics.cost)}</h3>
              </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 col-span-1 row-span-1">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Avg. Cost Per View</p>
                <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                  <MousePointerClick className="w-4 h-4 text-green-600" />
                </div>
              </div>
              <div className="mt-1">
                <h3 className="text-2xl font-semibold text-gray-900">₺{formatNumber(summaryMetrics.avg_cost_per_view)}</h3>
              </div>
          </div>
        </div>
      </div>
      
      {/* Campaign Performance Table with Ad Groups */}
      <Card className="rounded-2xl overflow-hidden bg-white">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Video Campaign Performance</h2>
              <p className="text-sm text-gray-500 mt-1">Detailed metrics for YouTube and video ad campaigns</p>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {dataLoading ? (
            <div className="min-h-[400px] flex items-center justify-center">
              <div className="loading-logo-container">
                <img 
                  src="/logoicon.png" 
                  alt="Glowytics Logo" 
                  className="loading-logo"
                />
              </div>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="min-h-[400px] flex justify-center items-center p-12">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Video className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-600 mb-2 font-medium">No Video Campaign Data</p>
                <p className="text-sm text-gray-500">No video performance data found for the selected date range.</p>
              </div>
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th
                    className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('name')}
                  >
                    Campaign / Ad Group
                    {sortField === 'name' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('budget')}
                  >
                    Budget
                    {sortField === 'budget' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('cost')}
                  >
                    Cost
                    {sortField === 'cost' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('cpm')}
                  >
                    CPM
                    {sortField === 'cpm' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('impressions')}
                  >
                    Impressions
                    {sortField === 'impressions' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                   <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('video_views')}
                  >
                    Video Views
                    {sortField === 'video_views' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('video_view_rate')}
                  >
                    View Rate
                    {sortField === 'video_view_rate' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('cost_per_view')}
                  >
                    Cost/View
                    {sortField === 'cost_per_view' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('clicks')}
                  >
                    Clicks
                    {sortField === 'clicks' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversions')}
                  >
                    Conversions
                    {sortField === 'conversions' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversion_value')}
                  >
                    Conv. Value
                    {sortField === 'conversion_value' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200"
                  >
                    Quartile Rates
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedCampaigns.map((campaign) => (
                  <React.Fragment key={campaign.id}>
                    {/* Campaign Row */}
                    <tr
                      className={`group/row hover:bg-blue-50/30 cursor-pointer transition-colors ${
                        expandedCampaigns.has(campaign.id) ? 'bg-blue-50/50' : 'bg-white'
                      }`}
                      onClick={(e) => toggleCampaign(campaign.id, e)}
                    >
                      <td className="py-2.5 px-3 border-r border-gray-200 relative">
                        <div className="flex items-center gap-2">
                          {/* Expansion Icon */}
                          <div className="flex items-center justify-center w-5 h-5">
                            {expandedCampaigns.has(campaign.id) ?
                              <ChevronDown className="w-4 h-4 text-blue-500" /> :
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            }
                          </div>
                          {/* Status dot + hover arrow + Campaign Name */}
                          {(() => {
                            const live = liveDataMap[String(campaign.id)];
                            const resolvedStatus = cleanStatus(live?.status || campaign.status || '');
                            const liveId = live?.id || campaign.id;
                            return (
                              <>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setOpenStatusDropdown(openStatusDropdown === campaign.id ? null : campaign.id); }}
                                  disabled={updatingId === campaign.id}
                                  className="flex-shrink-0 -ml-1 flex items-center gap-0.5 px-1 py-1 rounded-md hover:bg-gray-100 transition-colors focus:outline-none group/dot"
                                  title={resolvedStatus === 'ENABLED' ? 'Enabled — click to change' : 'Paused — click to change'}
                                >
                                  {updatingId === campaign.id ? (
                                    <Loader2 className="w-2.5 h-2.5 animate-spin text-gray-500" />
                                  ) : (
                                    <div className={`w-2.5 h-2.5 rounded-full ${resolvedStatus === 'ENABLED' ? 'bg-green-500 shadow-[0_0_0_3px_rgba(34,197,94,0.15)]' : 'bg-gray-400 shadow-[0_0_0_3px_rgba(156,163,175,0.15)]'}`} />
                                  )}
                                  <ChevronDown className="w-3 h-3 text-gray-400 opacity-0 group-hover/row:opacity-100 transition-opacity" />
                                </button>
                                {openStatusDropdown === campaign.id && (
                                  <>
                                    <div className="fixed inset-0 z-10 cursor-default" onClick={() => setOpenStatusDropdown(null)} />
                                    <div className="absolute top-full left-8 mt-1 w-40 bg-white rounded-xl shadow-xl border border-gray-100 z-20 py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top-left">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenStatusDropdown(null);
                                          setConfirmDialog({
                                            title: 'Enable Campaign',
                                            message: `Are you sure you want to enable campaign "${campaign.name}"?`,
                                            variant: 'enable',
                                            onConfirm: () => {
                                              updateEntity('campaign', liveId, 'status', 'ENABLED', campaign.id, resolvedStatus, campaign.name);
                                              setConfirmDialog(null);
                                            }
                                          });
                                        }}
                                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors
                                          ${resolvedStatus === 'ENABLED' ? 'text-green-600 bg-green-50/50' : 'text-gray-700'}
                                        `}
                                      >
                                        <div className="w-2 h-2 rounded-full bg-green-500" />
                                        <span>Enable</span>
                                        {resolvedStatus === 'ENABLED' && <Check className="w-3.5 h-3.5 ml-auto" />}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenStatusDropdown(null);
                                          setConfirmDialog({
                                            title: 'Pause Campaign',
                                            message: `Are you sure you want to pause campaign "${campaign.name}"?`,
                                            variant: 'pause',
                                            onConfirm: () => {
                                              updateEntity('campaign', liveId, 'status', 'PAUSED', campaign.id, resolvedStatus, campaign.name);
                                              setConfirmDialog(null);
                                            }
                                          });
                                        }}
                                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors
                                          ${resolvedStatus === 'PAUSED' ? 'text-gray-900 bg-gray-50' : 'text-gray-700'}
                                        `}
                                      >
                                        <div className="w-2 h-2 rounded-full bg-gray-400" />
                                        <span>Pause</span>
                                        {resolvedStatus === 'PAUSED' && <Check className="w-3.5 h-3.5 ml-auto" />}
                                      </button>
                                    </div>
                                  </>
                                )}
                              </>
                            );
                          })()}
                          {/* Campaign icon (keep original video.svg) */}
                          <div className="w-6 h-6 bg-transparent flex items-center justify-center">
                            <img
                              src="/video.svg"
                              alt="Video Campaign"
                              className="w-4 h-4"
                            />
                          </div>
                          <p className="text-[13px] text-gray-900">{campaign.name}</p>
                        </div>
                      </td>
                      {/* Budget cell with inline edit */}
                      <td className="px-3 py-2.5 whitespace-nowrap text-right border-r border-gray-200" onClick={e => e.stopPropagation()}>
                        {(() => {
                          const live = liveDataMap[String(campaign.id)];
                          const isEditing = editBudget?.id === campaign.id;
                          const displayBudget = live?.budget != null ? live.budget : campaign.budget;
                          const hasBudgetId = !!(live?.budgetId || campaign.budget_id);

                          if (isEditing) {
                            return (
                              <div className="flex items-center justify-end gap-2">
                                <input
                                  type="number"
                                  value={editBudget.value}
                                  onChange={(e) => setEditBudget({ ...editBudget, value: e.target.value })}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveBudget(campaign.id, campaign.name);
                                    if (e.key === 'Escape') setEditBudget(null);
                                  }}
                                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                                  autoFocus
                                />
                                <button onClick={() => handleSaveBudget(campaign.id, campaign.name)} className="p-1 text-green-600 hover:bg-green-50 rounded">
                                  <Check className="w-4 h-4" />
                                </button>
                                <button onClick={() => setEditBudget(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          }

                          return (
                            <div className="flex items-center justify-end gap-2 group">
                              <span className="text-sm text-gray-900 font-medium">
                                {displayBudget != null ? `₺${displayBudget.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : isLoadingLive ? <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 inline" /> : '—'}
                              </span>
                              {hasBudgetId && (
                                <button
                                  onClick={() => setEditBudget({ id: campaign.id, value: displayBudget || 0 })}
                                  disabled={updatingId === campaign.id}
                                  className="opacity-0 group-hover/row:opacity-100 p-1 text-gray-400 hover:text-blue-600 transition-opacity"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{formatNumber(campaign.cost)}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{formatNumber((campaign.impressions || 0) > 0 ? (campaign.cost / campaign.impressions) * 1000 : 0)}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.impressions || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.video_views || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{formatNumber(campaign.video_view_rate)}%</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{formatNumber(campaign.cost_per_view)}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.clicks || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{campaign.conversions || 0}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{formatNumber(campaign.conversion_value)}</td>
                      <td className="py-2.5 px-3 text-right">
                        <div className="flex flex-col text-xs text-gray-600 space-y-0.5">
                          <span>25%: <span className="font-medium text-gray-800">{formatNumber(campaign.video_quartile_p25_rate)}%</span></span>
                          <span>50%: <span className="font-medium text-gray-800">{formatNumber(campaign.video_quartile_p50_rate)}%</span></span>
                          <span>75%: <span className="font-medium text-gray-800">{formatNumber(campaign.video_quartile_p75_rate)}%</span></span>
                          <span>100%: <span className="font-medium text-gray-800">{formatNumber(campaign.video_quartile_p100_rate)}%</span></span>
                        </div>
                      </td>
                    </tr>

                    {/* Ad Groups Section */}
                    {expandedCampaigns.has(campaign.id) && campaign.adGroups && (
                      <>
                        {sortAdGroups(campaign.adGroups, sortField, sortDirection).map((adGroup) => (
                          <tr key={adGroup.id} className="hover:bg-blue-50/20 bg-blue-50/5">
                            <td className="py-2 px-3 border-r border-gray-200">
                              <div className="flex items-center gap-2 pl-10">
                                <div className="w-5 h-5 flex items-center justify-center">
                                  <img src="/enableicon.svg" alt="Ad Group" className="w-3 h-3" />
                                </div>
                                <div>
                                  <p className="text-[13px] font-medium text-gray-800">{adGroup.name}</p>
                                </div>
                              </div>
                            </td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{formatNumber(adGroup.cost)}</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{formatNumber((adGroup.impressions || 0) > 0 ? (adGroup.cost / adGroup.impressions) * 1000 : 0)}</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{(adGroup.impressions || 0).toLocaleString()}</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{(adGroup.video_views || 0).toLocaleString()}</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{formatNumber(adGroup.video_view_rate)}%</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{formatNumber(adGroup.cost_per_view)}</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{(adGroup.clicks || 0).toLocaleString()}</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{adGroup.conversions || 0}</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{formatNumber(adGroup.conversion_value)}</td>
                            <td className="py-2 px-3 text-right">
                              <div className="flex flex-col text-xs text-gray-600 space-y-0.5">
                                <span>25%: <span className="font-medium text-gray-800">{formatNumber(adGroup.video_quartile_p25_rate)}%</span></span>
                                <span>50%: <span className="font-medium text-gray-800">{formatNumber(adGroup.video_quartile_p50_rate)}%</span></span>
                                <span>75%: <span className="font-medium text-gray-800">{formatNumber(adGroup.video_quartile_p75_rate)}%</span></span>
                                <span>100%: <span className="font-medium text-gray-800">{formatNumber(adGroup.video_quartile_p100_rate)}%</span></span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
              {/* Toplam Satırı - Tüm kampanyaların toplamı */}
              <tfoot>
                <tr className="bg-gray-100 border-t-2 border-gray-300">
                  <td className="py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    Total
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-400">
                    —
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{formatNumber(campaigns.reduce((total, campaign) => total + (campaign.cost || 0), 0))}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {campaigns.reduce((total, campaign) => total + (campaign.impressions || 0), 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {campaigns.reduce((total, campaign) => total + (campaign.video_views || 0), 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {(() => {
                      const totalImpressions = campaigns.reduce((total, campaign) => total + (campaign.impressions || 0), 0);
                      const totalViews = campaigns.reduce((total, campaign) => total + (campaign.video_views || 0), 0);
                      return totalImpressions > 0 ? formatNumber((totalViews / totalImpressions) * 100) : '0.00';
                    })()}%
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{(() => {
                      const totalCost = campaigns.reduce((total, campaign) => total + (campaign.cost || 0), 0);
                      const totalViews = campaigns.reduce((total, campaign) => total + (campaign.video_views || 0), 0);
                      return totalViews > 0 ? formatNumber(totalCost / totalViews) : '0.00';
                    })()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {campaigns.reduce((total, campaign) => total + (campaign.clicks || 0), 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {campaigns.reduce((total, campaign) => total + (campaign.conversions || 0), 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{formatNumber(campaigns.reduce((total, campaign) => total + (campaign.conversion_value || 0), 0))}
                  </td>
                  <td className="py-3 px-3 text-right font-medium text-[13px] text-gray-900">
                    -
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </Card>

      {/* Monthly Breakdown Table */}
      <Card className="rounded-2xl overflow-hidden bg-white">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-500" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Monthly Breakdown</h2>
              <p className="text-sm text-gray-500 mt-1">Aggregated video campaign metrics by month</p>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {dataLoading ? (
            <div className="min-h-[200px] flex items-center justify-center">
              <div className="loading-logo-container">
                <img src="/logoicon.png" alt="Glowytics Logo" className="loading-logo" />
              </div>
            </div>
          ) : sortedMonthlyData.length === 0 ? (
            <div className="min-h-[200px] flex justify-center items-center p-12">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Calendar className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-600 mb-2 font-medium">No Monthly Data</p>
                <p className="text-sm text-gray-500">No data available for the selected date range.</p>
              </div>
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50" onClick={() => handleMonthlySort('month')}>
                    Month{monthlySortField === 'month' && <span className="ml-1">{monthlySortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50" onClick={() => handleMonthlySort('cost')}>
                    Cost{monthlySortField === 'cost' && <span className="ml-1">{monthlySortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50" onClick={() => handleMonthlySort('cpm')}>
                    CPM{monthlySortField === 'cpm' && <span className="ml-1">{monthlySortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50" onClick={() => handleMonthlySort('impressions')}>
                    Impressions{monthlySortField === 'impressions' && <span className="ml-1">{monthlySortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50" onClick={() => handleMonthlySort('video_views')}>
                    Video Views{monthlySortField === 'video_views' && <span className="ml-1">{monthlySortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50" onClick={() => handleMonthlySort('view_rate')}>
                    View Rate{monthlySortField === 'view_rate' && <span className="ml-1">{monthlySortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50" onClick={() => handleMonthlySort('cost_per_view')}>
                    Cost/View{monthlySortField === 'cost_per_view' && <span className="ml-1">{monthlySortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50" onClick={() => handleMonthlySort('clicks')}>
                    Clicks{monthlySortField === 'clicks' && <span className="ml-1">{monthlySortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50" onClick={() => handleMonthlySort('conversions')}>
                    Conversions{monthlySortField === 'conversions' && <span className="ml-1">{monthlySortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50" onClick={() => handleMonthlySort('conversion_value')}>
                    Conv. Value{monthlySortField === 'conversion_value' && <span className="ml-1">{monthlySortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">
                    Quartile Rates
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedMonthlyData.map((m) => (
                  <tr key={m.month} className="hover:bg-blue-50/30 transition-colors">
                    <td className="py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{formatMonthLabel(m.month)}</td>
                    <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{formatNumber(m.cost)}</td>
                    <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{formatNumber(m.cpm)}</td>
                    <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(m.impressions || 0).toLocaleString()}</td>
                    <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(m.video_views || 0).toLocaleString()}</td>
                    <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{formatNumber(m.view_rate)}%</td>
                    <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{formatNumber(m.cost_per_view)}</td>
                    <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(m.clicks || 0).toLocaleString()}</td>
                    <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{m.conversions || 0}</td>
                    <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{formatNumber(m.conversion_value)}</td>
                    <td className="py-2.5 px-3 text-right">
                      <div className="flex flex-col text-xs text-gray-600 space-y-0.5">
                        <span>25%: <span className="font-medium text-gray-800">{formatNumber(m.video_quartile_p25_rate)}%</span></span>
                        <span>50%: <span className="font-medium text-gray-800">{formatNumber(m.video_quartile_p50_rate)}%</span></span>
                        <span>75%: <span className="font-medium text-gray-800">{formatNumber(m.video_quartile_p75_rate)}%</span></span>
                        <span>100%: <span className="font-medium text-gray-800">{formatNumber(m.video_quartile_p100_rate)}%</span></span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-100 border-t-2 border-gray-300">
                  <td className="py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">Grand Total</td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">₺{formatNumber(monthlyGrandTotal.cost)}</td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">₺{formatNumber(monthlyGrandTotal.impressions > 0 ? (monthlyGrandTotal.cost / monthlyGrandTotal.impressions) * 1000 : 0)}</td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">{monthlyGrandTotal.impressions.toLocaleString()}</td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">{monthlyGrandTotal.video_views.toLocaleString()}</td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {monthlyGrandTotal.impressions > 0 ? formatNumber((monthlyGrandTotal.video_views / monthlyGrandTotal.impressions) * 100) : '0.00'}%
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{monthlyGrandTotal.video_views > 0 ? formatNumber(monthlyGrandTotal.cost / monthlyGrandTotal.video_views) : '0.00'}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">{monthlyGrandTotal.clicks.toLocaleString()}</td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">{monthlyGrandTotal.conversions.toLocaleString()}</td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">₺{formatNumber(monthlyGrandTotal.conversion_value)}</td>
                  <td className="py-3 px-3 text-right font-medium text-[13px] text-gray-900">-</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </Card>

      {/* YouTube Video Previews - Active campaigns (DB-independent, live API) */}
      <Card className="rounded-2xl overflow-hidden bg-white mt-6">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">YouTube Video Previews</h2>
              <p className="text-sm text-gray-500 mt-1">Active YouTube campaign videos from Google Ads API</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          {videoPreviewsLoading ? (
            <div className="min-h-[200px] flex items-center justify-center">
              <div className="loading-logo-container">
                <img src="/logoicon.png" alt="Glowytics Logo" className="loading-logo" />
              </div>
            </div>
          ) : videoPreviews.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Video className="w-12 h-12 text-gray-300 mb-3" />
              <p className="text-gray-600 font-medium">No video previews</p>
              <p className="text-sm text-gray-500 text-center max-w-md mt-1">
                No active YouTube campaigns with video assets found for the selected date range.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {videoPreviews.map(({ videoId, campaignName }) => (
                <a
                  key={videoId}
                  href={`https://www.youtube.com/watch?v=${videoId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl overflow-hidden border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all"
                >
                  <div className="aspect-video bg-gray-100 relative">
                    <img
                      src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                      alt={campaignName || `Video ${videoId}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center">
                        <Play className="w-5 h-5 text-white ml-0.5" fill="currentColor" />
                      </div>
                    </div>
                  </div>
                  <div className="p-2 flex items-center justify-between gap-1">
                    <span className="text-xs text-gray-600 truncate flex-1" title={campaignName}>
                      {campaignName || videoId}
                    </span>
                    <ExternalLink className="w-3 h-3 text-gray-400 flex-shrink-0" />
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </Card>
      
      {/* Bottom Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card className="rounded-2xl overflow-hidden bg-white">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-medium text-gray-800">Top Video Categories by Cost</h3>
              <div className="text-xs text-gray-500">TRY</div>
            </div>
          </div>
          {isYoutubeLoading ? (
            <div className="p-5 min-h-64 flex items-center">
              <LoadingSkeleton lines={6} />
            </div>
          ) : (() => {
            // Base rows from rawData
            let rows = (youtubeData && Array.isArray(youtubeData.rawData)) ? youtubeData.rawData : [];
            // Only use dummy data for the special demo brand, not for all brands
            const currentBrandId = localStorage.getItem('currentBrandId');
            if (!rows.length && isDemoBrandId(currentBrandId)) {
              rows = buildDummyYoutubeData().rawData;
            }
            // Enforce custom date filter on client in case backend falls back
            if (dateRange === 'custom' && customStartDate && customEndDate && rows.some(r => r.date)) {
              const s = new Date(customStartDate);
              const e = new Date(customEndDate);
              e.setHours(23,59,59,999);
              rows = rows.filter(r => {
                const d = r.date ? new Date(r.date) : null;
                return d && d >= s && d <= e;
              });
            }
            // If still no rows after filtering, show empty state
            if (!Array.isArray(rows) || rows.length === 0) {
              return (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="text-gray-400 mb-2">
                    <Play className="w-12 h-12 mx-auto mb-2" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-700 mb-1">No category data</h3>
                  <p className="text-sm text-gray-500 text-center max-w-md">No YouTube video category costs found for the selected date range.</p>
                </div>
              );
            }
            const agg = new Map(); // name -> { cost, impressions }
            const topChannelsByCategory = new Map(); // name -> [{channel, cost, impressions}]
            // Build category aggregates and per-category channel breakdown
            for (const r of rows) {
              const name = (r.video_category_name || 'Unknown Category').trim();
              const cost = Number(r.cost || 0);
              const imps = Number(r.impressions || 0);
              const cur = agg.get(name) || { cost: 0, impressions: 0 };
              cur.cost += cost;
              cur.impressions += imps;
              agg.set(name, cur);

              // Channel/placement breakdown per category
              const placement = r.placement || r.channel_id || r.channel || '';
              if (placement) {
                if (!topChannelsByCategory.has(name)) topChannelsByCategory.set(name, new Map());
                const map = topChannelsByCategory.get(name);
                const ch = map.get(placement) || { channel: placement, cost: 0, impressions: 0, video_views: 0 };
                ch.cost += Number(r.cost || 0);
                ch.impressions += Number(r.impressions || 0);
                ch.video_views += Number(r.video_views || 0);
                map.set(placement, ch);
              }
            }
            const top = Array.from(agg.entries())
              .map(([name, v]) => ({ name, cost: v.cost, impressions: v.impressions }))
              .sort((a, b) => b.cost - a.cost);
            const totalCost = top.reduce((s, i) => s + i.cost, 0);
            const totalImpressions = top.reduce((s, i) => s + i.impressions, 0);
            const total = (totalCost || 1); // for bar width calc
            const totalCpm = totalImpressions > 0 ? (totalCost / totalImpressions) * 1000 : 0;
            return (
              <div className="p-5">
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {top.map((cat, idx) => {
                    const pct = Math.max(0, Math.min(100, (cat.cost / total) * 100));
                    const cpm = cat.impressions > 0 ? (cat.cost / cat.impressions) * 1000 : 0;
                    const isExpanded = expandedCategories.has(cat.name);
                    // Build top 10 channels list if available
                    let channels = [];
                    if (topChannelsByCategory.has(cat.name)) {
                      channels = Array.from(topChannelsByCategory.get(cat.name).values())
                        .sort((a,b) => b.cost - a.cost)
                        .slice(0, 10);
                    }
                    return (
                      <div key={cat.name} className="">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => {
                              const next = new Set(expandedCategories);
                              if (isExpanded) next.delete(cat.name); else next.add(cat.name);
                              setExpandedCategories(next);
                            }}
                            className="w-6 text-xs text-gray-500 tabular-nums text-left"
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            <span className="inline-block w-3">{isExpanded ? '▼' : '▶'}</span>
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate" title={cat.name}>{cat.name}</p>
                            <div className="w-full h-2 bg-gray-100 rounded-full mt-1">
                              <div className="h-2 rounded-full bg-blue-500" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                          <div className="text-right ml-2 whitespace-nowrap">
                            <div className="text-sm font-semibold text-gray-900">₺{formatNumber(cat.cost, { maximumFractionDigits: 0 })}</div>
                            <div className="flex items-center gap-2 justify-end mt-1">
                              <span className="text-[11px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-medium">CPM ₺{formatNumber(cpm, { maximumFractionDigits: 0 })}</span>
                              <span className="text-[11px] text-gray-500">{(cat.impressions || 0).toLocaleString()} imp</span>
                            </div>
                          </div>
                        </div>

                        {isExpanded && channels.length > 0 && (
                          <div className="mt-2 ml-6 border-l border-gray-200 pl-4">
                            <div className="text-[11px] text-gray-500 mb-1">Top Channels</div>
                            <div className="space-y-1 max-h-48 overflow-y-auto">
                              {channels.map((ch, i) => {
                                const cpmCh = ch.impressions > 0 ? (ch.cost / ch.impressions) * 1000 : 0;
                                // If we have channelNames mapping from youtubeData, prefer it
                                const titleMap = (youtubeData && youtubeData.channelNames) ? youtubeData.channelNames : {};
                                const display = titleMap[ch.channel] || ch.channel;
                                const isChannelId = ch.channel && ch.channel.length > 20;
                                return (
                                  <div key={`${cat.name}-${ch.channel}-${i}`} className="flex items-center justify-between py-1">
                                    <div className="flex items-center gap-2 min-w-0">
                                      {isChannelId && (
                                        <a href={`https://www.youtube.com/channel/${ch.channel}`} target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-800" title="YouTube channel">
                                          <img src="/youtube.svg" alt="YouTube" className="w-3.5 h-3.5" />
                                        </a>
                                      )}
                                      <span className="text-xs text-gray-800 truncate max-w-[240px]" title={display}>{display}</span>
                                    </div>
                                    <div className="text-right whitespace-nowrap">
                                      <span className="text-xs font-medium text-gray-900">₺{formatNumber(ch.cost, { maximumFractionDigits: 0 })}</span>
                                      <span className="text-[10px] text-gray-500 ml-2">{(ch.impressions || 0).toLocaleString()} imp</span>
                                      <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full ml-2">CPM ₺{formatNumber(cpmCh, { maximumFractionDigits: 0 })}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Totals row */}
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-700">Totals</div>
                    <div className="flex items-center gap-6 text-right">
                      <div className="text-sm text-gray-600"><span className="font-semibold text-gray-900">₺{formatNumber(totalCost, { maximumFractionDigits: 0 })}</span> total cost</div>
                      <div className="text-sm text-gray-600"><span className="font-semibold text-gray-900">{(totalImpressions || 0).toLocaleString()}</span> impressions</div>
                      <div className="text-sm text-gray-600"><span className="font-semibold text-gray-900">₺{formatNumber(totalCpm, { maximumFractionDigits: 0 })}</span> CPM</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </Card>
        
        <Card className="rounded-2xl overflow-hidden bg-white">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-medium text-gray-800">Top YouTube Placements</h3>
              <select className="text-sm border-0 bg-gray-50 rounded-lg px-3 py-1.5">
                <option value="impressions">By Impressions</option>
                <option value="video_views">By Video Views</option>
              </select>
            </div>
          </div>
          
          {isYoutubeLoading ? (
            <div className="p-5 min-h-64 flex items-center">
              <LoadingSkeleton lines={8} />
            </div>
          ) : (() => {
            // Build candidate rows with custom-date filtering
            const currentBrandId = localStorage.getItem('currentBrandId');
            let baseRows = [];
            if (Array.isArray(youtubeData?.rawData) && youtubeData.rawData.length > 0) {
              baseRows = youtubeData.rawData;
            } else if (Array.isArray(youtubeData?.placements) && youtubeData.placements.length > 0) {
              baseRows = youtubeData.placements;
            } else if (dateRange !== 'custom') {
              baseRows = (youtubeData && Array.isArray(youtubeData.topPlacements)) ? youtubeData.topPlacements : [];
            }
            // Only use dummy data for the special demo brand
            if (!baseRows.length && isDemoBrandId(currentBrandId)) {
              const dummy = buildDummyYoutubeData();
              baseRows = dummy.rawData;
            }
            if (dateRange === 'custom' && customStartDate && customEndDate && baseRows.some(r => r.date)) {
              const s = new Date(customStartDate);
              const e = new Date(customEndDate);
              e.setHours(23,59,59,999);
              baseRows = baseRows.filter(r => {
                const d = r.date ? new Date(r.date) : null;
                return d && d >= s && d <= e;
              });
            }
            if (!baseRows.length) return false;
            return true;
          })() ? (
            <div className="p-5">
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {(() => {
                  // 1) Satır kaynağı + custom filtre
                  const currentBrandId = localStorage.getItem('currentBrandId');
                  let rows = [];
                  if (Array.isArray(youtubeData?.rawData) && youtubeData.rawData.length > 0) {
                    rows = youtubeData.rawData;
                  } else if (Array.isArray(youtubeData?.placements) && youtubeData.placements.length > 0) {
                    rows = youtubeData.placements;
                  } else if (dateRange !== 'custom') {
                    rows = (youtubeData && Array.isArray(youtubeData.topPlacements)) ? youtubeData.topPlacements : [];
                  }
                  // Only use dummy data for the special demo brand
                  if ((!rows || rows.length === 0) && isDemoBrandId(currentBrandId)) {
                    rows = buildDummyYoutubeData().rawData;
                  }
                  if (dateRange === 'custom' && customStartDate && customEndDate && rows.some(r => r.date)) {
                    const s = new Date(customStartDate);
                    const e = new Date(customEndDate);
                    e.setHours(23,59,59,999);
                    rows = rows.filter(r => {
                      const d = r.date ? new Date(r.date) : null;
                      return d && d >= s && d <= e;
                    });
                  }

                  // 2) Placement bazında kampanyadan bağımsız aggregate
                  const agg = new Map(); // placement -> { placement, impressions, video_views, cost }
                  for (const r of rows) {
                    const p = r.placement;
                    if (!p) continue;
                    const cur = agg.get(p) || { placement: p, impressions: 0, video_views: 0, cost: 0 };
                    cur.impressions += Number(r.impressions || 0);
                    cur.video_views += Number(r.video_views || 0);
                    cur.cost += Number(r.cost || 0);
                    agg.set(p, cur);
                  }

                  // 3) Kanal başlıklarını belirle (DB'deki channel_title öncelikli, sonra fetched map)
                  const titleByPlacement = new Map();
                  if (Array.isArray(youtubeData?.rawData)) {
                    for (const r of youtubeData.rawData) {
                      const p = r.placement;
                      const t = (r.channel_title || '').trim();
                      if (p && t && !titleByPlacement.has(p)) titleByPlacement.set(p, t);
                    }
                  }

                  const list = Array.from(agg.values())
                    .sort((a, b) => (b.impressions || 0) - (a.impressions || 0))
                    .slice(0, 100);

                  const totals = list.reduce((acc, it) => {
                    acc.cost += Number(it.cost || 0);
                    acc.impressions += Number(it.impressions || 0);
                    return acc;
                  }, { cost: 0, impressions: 0 });
                  // Try to derive total revenue (conversion_value) if available
                  let totalRevenue = 0;
                  if (Array.isArray(youtubeData?.rawData) && youtubeData.rawData.length > 0) {
                    totalRevenue = youtubeData.rawData.reduce((s, r) => s + Number(r.conversion_value || 0), 0);
                  } else if (Array.isArray(campaigns) && campaigns.length > 0) {
                    totalRevenue = campaigns.reduce((s, c) => s + Number(c.conversion_value || 0), 0);
                  }
                  const totalCpm = totals.impressions > 0 ? (totals.cost / totals.impressions) * 1000 : 0;

                  return [
                    ...list.map((pl) => {
                    const placementId = pl.placement;
                    const channelNameFromDb = titleByPlacement.get(placementId);
                    const channelNameFetched = (youtubeData && youtubeData.channelNames && youtubeData.channelNames[placementId])
                      ? youtubeData.channelNames[placementId]
                      : null;
                    const channelName = channelNameFromDb || channelNameFetched || placementId;
                    const isChannelId = placementId && placementId.length > 20;
                    const cpm = pl.impressions > 0 ? (pl.cost / pl.impressions) * 1000 : 0;

                    return (
                      <div key={placementId} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {isChannelId && (
                              <a
                                href={`https://www.youtube.com/channel/${placementId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-red-600 hover:text-red-800"
                                title="YouTube channel"
                              >
                                <img src="/youtube.svg" alt="YouTube" className="w-4 h-4" />
                              </a>
                            )}
                            <div className="flex-1 min-w-0">
                              {isChannelId ? (
                                <a
                                  href={`https://www.youtube.com/channel/${placementId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium text-gray-900 hover:text-red-600 truncate cursor-pointer transition-colors block"
                                  title={channelName}
                                >
                                  {channelName}
                                </a>
                              ) : (
                                <p className="text-sm font-medium text-gray-900 truncate" title={channelName}>
                                  {channelName}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-sm font-medium text-gray-900">{(pl.impressions || 0).toLocaleString()}</p>
                          <div className="flex items-center gap-2 justify-end">
                            <p className="text-xs text-gray-500">{(pl.video_views || 0).toLocaleString()} views</p>
                            <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full font-medium">
                              {pl.impressions > 0 ? ((pl.video_views / pl.impressions) * 100).toFixed(2) : '0.00'}% VR
                            </span>
                          </div>
                          <div className="flex items-center gap-3 justify-end mt-1">
                            <div className="text-xs text-gray-600">₺{formatNumber(pl.cost, { maximumFractionDigits: 0 })} total</div>
                            <span className="text-[11px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-medium">
                              CPM ₺{formatNumber(cpm, { maximumFractionDigits: 0 })}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }),
                    (
                      <div key="placements_totals" className="mt-5 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-gray-700">Totals</div>
                          <div className="flex items-center gap-6 text-right">
                            <div className="text-sm text-gray-600"><span className="font-semibold text-gray-900">₺{formatNumber(totals.cost, { maximumFractionDigits: 0 })}</span> total cost</div>
                            <div className="text-sm text-gray-600"><span className="font-semibold text-gray-900">₺{formatNumber(totalRevenue, { maximumFractionDigits: 0 })}</span> revenue</div>
                            <div className="text-sm text-gray-600"><span className="font-semibold text-gray-900">{(totals.impressions || 0).toLocaleString()}</span> impressions</div>
                            <div className="text-sm text-gray-600"><span className="font-semibold text-gray-900">₺{formatNumber(totalCpm, { maximumFractionDigits: 0 })}</span> CPM</div>
                          </div>
                        </div>
                      </div>
                    )
                  ];
                })()}
              </div>
              
              {youtubeData && youtubeData.summary && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  {(() => {
                  // Derive totals for Cost, Impressions and CPM from placements/rawData
                  let rows = [];
                  if (Array.isArray(youtubeData?.rawData) && youtubeData.rawData.length > 0) {
                    rows = youtubeData.rawData;
                  } else if (Array.isArray(youtubeData?.placements) && youtubeData.placements.length > 0) {
                    rows = youtubeData.placements;
                  } else if (dateRange !== 'custom') {
                    rows = (youtubeData && Array.isArray(youtubeData.topPlacements)) ? youtubeData.topPlacements : [];
                  }
                  if (dateRange === 'custom' && customStartDate && customEndDate && rows.some(r => r.date)) {
                    const s = new Date(customStartDate);
                    const e = new Date(customEndDate);
                    e.setHours(23,59,59,999);
                    rows = rows.filter(r => {
                      const d = r.date ? new Date(r.date) : null;
                      return d && d >= s && d <= e;
                    });
                  }

                    const totals = rows.reduce((acc, r) => {
                      acc.cost += Number(r.cost || 0);
                      acc.impressions += Number(r.impressions || 0);
                      return acc;
                    }, { cost: 0, impressions: 0 });
                    const totalCpm = totals.impressions > 0 ? (totals.cost / totals.impressions) * 1000 : 0;

                    return (
                      <div className="grid grid-cols-5 gap-4 text-center">
                        <div>
                          <p className="text-xs text-gray-500">Total Placements</p>
                          <p className="text-lg font-semibold text-gray-900">{(youtubeData.summary.totalPlacements || 0)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Avg. View Rate</p>
                          <p className="text-lg font-semibold text-gray-900">{(youtubeData.summary.avgVideoViewRate || 0)}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Total Cost</p>
                          <p className="text-lg font-semibold text-gray-900">₺{formatNumber(totals.cost, { maximumFractionDigits: 0 })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Impressions</p>
                          <p className="text-lg font-semibold text-gray-900">{(totals.impressions || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">CPM</p>
                          <p className="text-lg font-semibold text-gray-900">₺{formatNumber(totalCpm, { maximumFractionDigits: 0 })}</p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="text-gray-400 mb-2">
                <Play className="w-12 h-12 mx-auto mb-2" />
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-1">No data available</h3>
              <p className="text-sm text-gray-500 text-center max-w-md">
                No YouTube placements data found for the selected date range. Please try a different date range or check back later.
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* Custom date modal removed — <DateRangeFilter /> owns the calendar popover now */}

      {/* Change History Slide-over */}
      {showChangeHistory && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowChangeHistory(false)} />
          <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <History className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Change History</h2>
                  <p className="text-xs text-gray-500">{changeHistoryTotal} total changes</p>
                </div>
              </div>
              <button onClick={() => setShowChangeHistory(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="px-6 py-3 border-b border-gray-50 flex gap-2">
              {[
                { key: 'all', label: 'All' },
                { key: 'status_change', label: 'Status' },
                { key: 'budget_change', label: 'Budget' }
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setChangeHistoryFilter(f.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    changeHistoryFilter === f.key
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {changeHistoryLoading && changeHistoryData.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                </div>
              ) : changeHistoryData.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No changes recorded yet</p>
                  <p className="text-xs text-gray-400 mt-1">Changes to campaigns will appear here</p>
                </div>
              ) : (
                <>
                  {changeHistoryData.map((item, idx) => {
                    const isStatus = item.action === 'status_change';
                    const isBudget = item.action === 'budget_change';
                    const timeAgo = (() => {
                      const diff = Date.now() - new Date(item.created_at).getTime();
                      const mins = Math.floor(diff / 60000);
                      if (mins < 1) return 'Just now';
                      if (mins < 60) return `${mins}m ago`;
                      const hours = Math.floor(mins / 60);
                      if (hours < 24) return `${hours}h ago`;
                      const days = Math.floor(hours / 24);
                      if (days < 7) return `${days}d ago`;
                      return new Date(item.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
                    })();
                    const formatValue = (val, field) => {
                      if (!val) return '—';
                      if (field === 'amount_micros' || field === 'daily_budget') {
                        const num = parseFloat(val) / 1000000;
                        return `₺${num.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
                      }
                      return val;
                    };
                    return (
                      <div key={item.id || idx} className={`p-3.5 rounded-xl border transition-colors ${item.success ? 'border-gray-100 bg-white hover:bg-gray-50/50' : 'border-red-100 bg-red-50/30'}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5 ${
                              !item.success ? 'bg-red-100' : isStatus ? 'bg-amber-100' : isBudget ? 'bg-blue-100' : 'bg-gray-100'
                            }`}>
                              {!item.success ? <XCircle className="w-4 h-4 text-red-500" /> :
                               isStatus ? <Activity className="w-4 h-4 text-amber-600" /> :
                               <DollarSign className="w-4 h-4 text-blue-600" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider ${
                                  isStatus ? 'bg-amber-100 text-amber-700' : isBudget ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {isStatus ? 'Status' : isBudget ? 'Budget' : item.action}
                                </span>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  item.platform === 'GOOGLE_ADS' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'
                                }`}>
                                  {item.platform === 'GOOGLE_ADS' ? 'Google' : 'Meta'}
                                </span>
                              </div>
                              {item.entity_name && (
                                <p className="text-sm font-medium text-gray-800 mt-1 truncate">{item.entity_name}</p>
                              )}
                              <div className="flex items-center gap-1.5 mt-1 text-xs">
                                <span className="text-gray-400">{formatValue(item.old_value, item.field_name)}</span>
                                <span className="text-gray-300">→</span>
                                <span className={`font-medium ${item.success ? 'text-gray-700' : 'text-red-600'}`}>
                                  {formatValue(item.new_value, item.field_name)}
                                </span>
                              </div>
                              {!item.success && item.error_message && (
                                <p className="text-xs text-red-500 mt-1 truncate">{item.error_message}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <div className="flex items-center gap-1 text-[11px] text-gray-400">
                              <Clock className="w-3 h-3" />
                              {timeAgo}
                            </div>
                            {item.user_email && (
                              <p className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[120px]">{item.user_name || item.user_email}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {changeHistoryData.length < changeHistoryTotal && (
                    <button
                      onClick={() => fetchChangeHistory(changeHistoryData.length, true)}
                      disabled={changeHistoryLoading}
                      className="w-full py-2.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {changeHistoryLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Load More ({changeHistoryTotal - changeHistoryData.length} remaining)
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}